class PostcodeService {
  API_BASE_URL = "";

  constructor() {
    this.API_BASE_URL = "https://api.postcodes.io";
  }

  cleanPostcode(postcode: string) {
    return postcode.replace(/\s+/g, '').toUpperCase();
  }

  public async validatePostcode(postcode: string) {
    try {
      const postcodeList = postcode.includes(',')
        ? postcode.split(',').map(p => p.trim())
        : [postcode];


      if (postcodeList.length === 1) {
        return await this.validateSinglePostcode(postcodeList[0]);
      }

      return await this.validateBulkPostcodes(postcodeList);

    } catch (error) {
      console.log(error);
      throw new Error(`Validation failed for ${postcode}: ${error.message}`);
    }
  }

  private async validateSinglePostcode(postcode: string) {
    const cleanedPostcode = this.cleanPostcode(postcode);
    const response = await fetch(
      `${this.API_BASE_URL}/postcodes/${encodeURIComponent(cleanedPostcode)}/validate`
    );

    if (!response.ok) {
      throw new Error(`API request failed for postcode: ${cleanedPostcode}`);
    }

    const data = await response.json();

    return {
      postcode: cleanedPostcode,
      isValid: data.result,
      originalInput: postcode,
      results: [{
        postcode: cleanedPostcode,
        isValid: data.result,
        originalInput: postcode
      }]
    };
  }

  private async validateBulkPostcodes(postcodeList: string[]) {
    const cleanedPostcodes = postcodeList.map(p => this.cleanPostcode(p));
    
    const response = await fetch(`${this.API_BASE_URL}/postcodes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        postcodes: cleanedPostcodes
      })
    });

    if (!response.ok) {
      throw new Error(`Bulk API request failed`);
    }

    const data = await response.json();

    const results = data.result.map((item: any, index: number) => ({
      postcode: cleanedPostcodes[index],
      isValid: item.result !== null,
      originalInput: postcodeList[index],
      data: item.result
    }));

    // Return format consistent with single postcode validation
    const validResults = results.filter((r: any) => r.isValid);
    const hasAnyValid = validResults.length > 0;

    return {
      postcode: cleanedPostcodes.join(', '),
      isValid: hasAnyValid,
      originalInput: postcodeList.join(', '),
      results: results,
      validCount: validResults.length,
      totalCount: results.length
    };
  }

  async getPostcodeCoordinates(postcode: string) {
    try {
      const cleanedPostcode = this.cleanPostcode(postcode);
      const response = await fetch(
        `${this.API_BASE_URL}/postcodes/${encodeURIComponent(cleanedPostcode)}`
      );

      if (!response.ok) {
        throw new Error(`Invalid postcode: ${cleanedPostcode}`);
      }

      const data = await response.json();

      return {
        latitude: data.result.latitude,
        longitude: data.result.longitude
      };
    } catch (error) {
      throw new Error(`Error fetching coordinates for ${postcode}: ${error.message}`);
    }
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }

  toRadians(degrees: number) {
    return degrees * (Math.PI / 180);
  }

  public async getDistanceBetweenPostcodes(postcode1: string, postcode2: string) {
    try {
      // Validate the first (searched) postcode
      const validation1 = await this.validatePostcode(postcode1);
      if (!validation1.isValid) {
        throw new Error(`Invalid postcode: ${validation1.postcode}`);
      }
  
      // Check if postcode2 is concatenated (contains a comma)
      const postcodeList = postcode2.includes(',')
        ? postcode2.split(',').map(p => p.trim())
        : [postcode2];
  
      // Validate each postcode in the list
      const validations = await Promise.all(
        postcodeList.map(p => this.validatePostcode(p).catch(err => ({ isValid: false })))
      );
  
      // Keep only the valid ones using a type guard to ensure 'postcode' exists
      const validPostcodes = validations
        .filter(
          (v): v is { postcode: string; isValid: boolean; originalInput: string } =>
            v.isValid && 'postcode' in v
        )
        .map(v => v.postcode);
  
      if (validPostcodes.length === 0) {
        throw new Error('No valid postcode found in concatenated postcodes');
      }
  
      // Get coordinates for the first postcode (searched postcode)
      const coords1 = await this.getPostcodeCoordinates(validation1.postcode);
  
      // For each valid postcode from the referrer's list, calculate the distance
      const distances = await Promise.all(
        validPostcodes.map(async (pc) => {
          const coords2 = await this.getPostcodeCoordinates(pc);
          const distance = this.calculateDistance(
            coords1.latitude, coords1.longitude,
            coords2.latitude, coords2.longitude
          );
          return { distance: distance * 0.621371, postcode: pc, coords2 };
        })
      );
  
      // Select the postcode with the minimum distance
      const minDistanceObj = distances.reduce((min, current) =>
        current.distance < min.distance ? current : min, distances[0]
      );
  
      return {
        distance: minDistanceObj.distance === 0 ? 0.01 : minDistanceObj.distance,
        units: 'miles',
        postcodes: {
          from: validation1.postcode,
          to: minDistanceObj.postcode
        },
        coordinates: {
          from: coords1,
          to: minDistanceObj.coords2
        }
      };
    } catch (error) {
      throw new Error(`Failed to calculate distance: ${error.message}`);
    }
  }
}

const postcodeService = new PostcodeService();
export default postcodeService;
